import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import Select from 'components/forms/Select'
import { groupBy, isNil, isUndefined, keys, isNaN, floor, uniq } from 'lodash'
import useApplicationSetting, { intParser, listStringParser } from 'utils/customHook/useApplicationSetting'
import { AVERAGE, LAST_VALUE, MAX, MIN, P90, NITRATES_CODE, CATCHMENT_PESTICIDES, CATCHMENT_IRRELEVANT_PESTICIDES } from 'catchment/constants/CatchmentConstant'
import QualityAction from 'quality/actions/QualityAction'
import { ParameterGraphicEvolution, ParameterGraphicPercent, PieFamilyPesticides, SumParameterGraphicEvolution, SumParameterGraphicPercent } from './GraphicCompare'
import useListIndexed from 'utils/customHook/useListIndexed'
import { generateGradient, generateMultiGradient } from 'utils/ColorUtil'
import ParameterAction from 'referencial/components/parameter/actions/ParameterAction'
import useBoolean from 'utils/customHook/useBoolean'
import { getBeginingOfTheYear, getEndOfTheYear } from 'utils/DateUtil'
import PropTypes from 'prop-types'
import UnitAction from 'referencial/components/unit/actions/UnitAction'
import useActions from 'utils/customHook/useActions'
import ExportFileModal from 'components/modal/ExportFileModal'
import ExportAction from 'export/actions/ExportAction'
import Checkbox from 'components/forms/Checkbox'
import WatermassAction from 'referencial/components/watermass/actions/WatermassAction'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { INDICATORS_COLORS, JOB_COLOR, mainBlue, lightPink, errorColor, IAEAU_SCALE_COLORS } from 'utils/constants/ColorTheme'
import { CardTitle } from 'components/card/NewCard'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'

const THRESHOLDS_NITRATES = [10, 20, 30, 40, 50]
const THRESHOLDS_PESTICIDES = [0.01, 0.05, 0.1, 0.5, 2]
const THRESHOLDS_EVOLUTION = [80, 40, 10, -10, -40, -80]

const CardFilterEvolutionField = ({
    defaultFilters = {},
    onValidate = () => {},
}) => {
    const {
        parametersProps,
        departmentsProps,
        watermassesProps,
        catchments,
    } = useSelector(store => ({
        parametersProps: store.ParameterReducer.parameters,
        departmentsProps: store.CityReducer.departments,
        watermassesProps: store.WatermassReducer.watermasses,
        catchments: store.CatchmentReducer.catchments,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const watermassList = useMemo(() => {
        return watermassesProps.filter(w => w.euroCode && catchments.some(c => c.meCode === w.euroCode)).map(w => ({
            code: w.euroCode,
            name: `${showSandreCode ? `[${w.euroCode}]` : ''} ${w.name}`,
        }))
    }, [catchments, watermassesProps, showSandreCode])

    const indexedParameters = useListIndexed(parametersProps, 'code')

    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])

    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', intParser) ?? 1
    const defaultSecondEnd = useEndPeriod(intParser)
    const defaultSecondStart = useMemo(() => defaultSecondEnd - regroupment + 1, [defaultSecondEnd, regroupment])
    const defaultFirstEnd = useMemo(() => defaultSecondEnd - regroupment, [defaultSecondEnd, regroupment])
    const defaultFirstStart = useMemo(() => defaultSecondStart - regroupment, [defaultSecondStart, regroupment])

    const [filters, setFilters] = useState(defaultFilters)

    const onCancel = () => setFilters({
        firstStart: getBeginingOfTheYear(defaultFirstStart),
        firstEnd: getEndOfTheYear(defaultFirstEnd),
        secondStart: getBeginingOfTheYear(defaultSecondStart),
        secondEnd: getEndOfTheYear(defaultSecondEnd),
        criteria: LAST_VALUE,
        parameters: [],
        watermass: [],
        departments: [],
        selectedPriorities: [],
    })

    const criterias = useMemo(() => {
        return [
            { id: LAST_VALUE, label: i18n.lastValue },
            { id: MIN, label: i18n.min },
            { id: MAX, label: i18n.max },
            { id: AVERAGE, label: i18n.average },
            { id: P90, label: i18n.percentile90 },
        ]
    }, [])

    const parametersList = useMemo(() => {
        return pesticides.map(p => indexedParameters[p]).filter(p => !!p)
    }, [pesticides, indexedParameters])

    return (
        <Card>
            <CardContent>
                <Grid container columnSpacing={2} rowSpacing={0.5} alignItems='center'>
                    <Grid size={2.4}>
                        <SuperMultiAutocomplete
                            label={i18n.department}
                            options={departmentsProps}
                            keyValue='code'
                            onChange={v => setFilters({ ...filters, departments: v })}
                            values={filters.departments}
                            multiple
                            limit={1}
                        />
                    </Grid>
                    <Grid size={2.4}>
                        <SuperMultiAutocomplete
                            label={i18n.watermass}
                            options={watermassList}
                            keyValue='code'
                            onChange={v => setFilters({ ...filters, watermass: v })}
                            values={filters.watermass}
                            multiple
                            limit={1}
                        />
                    </Grid>
                    <Grid size={2.4}>
                        <SuperMultiAutocomplete
                            label={i18n.parameters}
                            options={parametersList}
                            values={filters.parameters}
                            onChange={v => setFilters({ ...filters, parameters: v })}
                            keyLabel={showSandreCode ? 'labelWithCode' : 'name'}
                            keyValue='code'
                            multiple
                            additionalKeysFilter={['name', 'code', 'shortLabel']}
                        />
                    </Grid>
                    <Grid size={2.4}>
                        <Select
                            label={i18n.criteria}
                            options={criterias}
                            value={filters.criteria}
                            onChange={v => setFilters({ ...filters, criteria: v })}
                            clearFunction
                        />
                    </Grid>
                    <Grid size={2.4}>
                        <SuperMultiAutocomplete
                            label={i18n.catchmentType}
                            options={[
                                { code: 'priority', name: i18n.havePriority },
                                { code: 'sensitive', name: i18n.sensitive },
                            ]}
                            keyValue='code'
                            onChange={v => setFilters({ ...filters, selectedPriorities: v })}
                            values={filters.selectedPriorities}
                            multiple
                            limit={2}
                        />
                    </Grid>

                    <Grid size={2}>
                        {i18n.firstPeriod}
                    </Grid>
                    <Grid size={2}>
                        <SimpleDatePicker
                            label={i18n.startDate}
                            value={filters.firstStart}
                            onChange={v => setFilters({ ...filters, firstStart: v })}
                            max={filters.firstEnd}
                        />
                    </Grid>
                    <Grid size={2}>
                        <SimpleDatePicker
                            label={i18n.endDate}
                            value={filters.firstEnd}
                            onChange={v => setFilters({ ...filters, firstEnd: v })}
                            min={filters.firstStart}
                        />
                    </Grid>

                    <Grid size={3}>
                        <Checkbox
                            label={i18n.useOnlyDataMainPoint}
                            checked={filters.useOnlyMainPoint}
                            onChange={v => setFilters({ ...filters, useOnlyMainPoint: v })}
                            truncate={false}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Checkbox
                            label={i18n.displayActiveSDAGE}
                            checked={filters.displayActiveSDAGE}
                            onChange={v => setFilters({ ...filters, displayActiveSDAGE: v })}
                            truncate={false}
                        />
                    </Grid>

                    <Grid size={2}>
                        {i18n.secondPeriod}
                    </Grid>
                    <Grid size={2}>
                        <SimpleDatePicker
                            label={i18n.startDate}
                            value={filters.secondStart}
                            onChange={v => setFilters({ ...filters, secondStart: v })}
                            max={filters.secondEnd}
                        />
                    </Grid>
                    <Grid size={2}>
                        <SimpleDatePicker
                            label={i18n.endDate}
                            value={filters.secondEnd}
                            onChange={v => setFilters({ ...filters, secondEnd: v })}
                            min={filters.secondStart}
                        />
                    </Grid>

                    <Grid size={3} >
                        <Button
                            variant='outlined'
                            fullWidth
                            onClick={onCancel}
                        >
                            { i18n.reinit }
                        </Button>
                    </Grid>
                    <Grid size={3}>
                        <Button
                            onClick={() => onValidate(filters)}
                            variant='contained'
                            fullWidth
                        >
                            {i18n.visualize}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

CardFilterEvolutionField.propTypes = {
    defaultFilters: PropTypes.shape({
        criteria: PropTypes.oneOf([LAST_VALUE, MIN, MAX, AVERAGE, P90]),
        parameters: PropTypes.arrayOf(PropTypes.string),
        firstStart: PropTypes.number,
        firstEnd: PropTypes.number,
        secondStart: PropTypes.number,
        secondEnd: PropTypes.number,
        useOnlyMainPoint: PropTypes.bool,
        departments: PropTypes.arrayOf(PropTypes.string),
        watermass: PropTypes.arrayOf(PropTypes.string),
        displayActiveSDAGE: PropTypes.bool,
    }),
    onValidate: PropTypes.func,
}

const ExportModal = ({
    isOpen = false,
    close = () => {},
    filters = {},
}) => {
    const dispatch = useDispatch()

    const exportEvolutionContamination = exportType => {
        const options = {
            exportType,
            usedCriteria: filters.criteria,
            additionalParameters: filters.parameters,
            startDate: filters.firstStart,
            endDate: filters.firstEnd,
            compareStartDate: filters.secondStart,
            compareEndDate: filters.secondEnd,
            useOnlyMainPoint: filters.useOnlyMainPoint,
            departments: filters.departments,
            watermass: filters.watermass,
            useActiveSDAGE: filters.displayActiveSDAGE,
        }
        dispatch(ExportAction.exportCatchmentEvolutions(options))
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={close}
            data={[{
                name: i18n.evolutionOfContaminations,
                formats: [{
                    type: i18n.excelFile,
                    callback: () => exportEvolutionContamination('xlsx'),
                }, {
                    type: i18n.csvFile,
                    callback: () => exportEvolutionContamination('csv'),
                }],
            }]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool,
    close: PropTypes.func,
    filters: PropTypes.shape({
        criteria: PropTypes.oneOf([LAST_VALUE, MIN, MAX, AVERAGE, P90]),
        parameters: PropTypes.arrayOf(PropTypes.string),
        firstStart: PropTypes.number,
        firstEnd: PropTypes.number,
        secondStart: PropTypes.number,
        secondEnd: PropTypes.number,
        useOnlyMainPoint: PropTypes.bool,
        departments: PropTypes.arrayOf(PropTypes.string),
        watermass: PropTypes.arrayOf(PropTypes.string),
        displayActiveSDAGE: PropTypes.bool,
    }),
}

const CatchmentsCompareApp = ({

}) => {
    const dispatch = useDispatch()

    const {
        value: isExportModalOpen,
        setTrue: openExportModal,
        setFalse: closeExportModal,
    } = useBoolean(false)

    useTitle(() => [{
        title: i18n.catchments,
        href: 'catchment',
    }, {
        title: i18n.comparing,
        href: 'catchment/compare',
    }], [])

    const {
        qualitometers,
        catchments,
        catchmentPoints,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        catchments: store.CatchmentReducer.catchments,
        catchmentPoints: store.CatchmentReducer.catchmentPoints,
    }), shallowEqual)

    const regroupment = useApplicationSetting('CATCHMENT_REGROUPING', intParser) ?? 1
    const secondEnd = useEndPeriod(intParser)
    const secondStart = useMemo(() => secondEnd - regroupment + 1, [secondEnd, regroupment])
    const firstEnd = useMemo(() => secondEnd - regroupment, [secondEnd, regroupment])
    const firstStart = useMemo(() => secondStart - regroupment, [secondStart, regroupment])
    const [parameters, setParameters] = useLocalStorage('CATCHMENT_COMPARISON_SELECTED_PARAMETERS', [])

    useEffect(() => {
        if (!qualitometers.length) {
            dispatch(QualityAction.fetchQualitometersLight())
        }
        dispatch(WatermassAction.fetchWatermasses())
        dispatch(ParameterAction.fetchParameterGroups())
        dispatch(ParameterAction.fetchParameterGroupLinks())
        dispatch(UnitAction.fetchUnits())
    }, [dispatch])

    const [filters, setFilters] = useState({
        firstStart: getBeginingOfTheYear(firstStart),
        firstEnd: getEndOfTheYear(firstEnd),
        secondStart: getBeginingOfTheYear(secondStart),
        secondEnd: getEndOfTheYear(secondEnd),
        criteria: LAST_VALUE,
        parameters,
        watermass: [],
        departments: [],
        selectedPriorities: [],
    })

    const qualitoIds = useMemo(() => {
        const searchActiveSDAGE = filters.displayActiveSDAGE ? catchments.filter(({ activeSdage }) => activeSdage) : catchments
        const watermassFilter = filters.watermass.length ? searchActiveSDAGE.filter(c => filters.watermass.includes(c.meCode)) : searchActiveSDAGE
        const catchmentFiltered = filters.departments.length ? watermassFilter.filter(c => filters.departments.includes(c.department)) : watermassFilter
        const prioritiesFilter = filters.selectedPriorities.length ? catchmentFiltered.filter(c => ((c.priority && filters.selectedPriorities.includes('priority')) || (c.sensitive && filters.selectedPriorities.includes('sensitive')))) : catchmentFiltered

        const filteredPoints = catchmentPoints.filter(p => prioritiesFilter.some(c => c.id === p.id))
        const groupByCatchment = groupBy(filteredPoints, 'id')
        const formattedPoints = keys(groupByCatchment).flatMap(key => {
            if (groupByCatchment[key].length === 1) {
                return [{
                    ...groupByCatchment[key][0],
                    mainPoint: true,
                }]
            }
            return groupByCatchment[key]
        })
        const formattedCatchmentPoints = filters.useOnlyMainPoint ? formattedPoints.filter(p => p.mainPoint) : formattedPoints

        return formattedCatchmentPoints.map(point => {
            return qualitometers.find(q => q.code === point.codeWithoutDesignation)?.id
        }).filter(q => !isUndefined(q)) || []
    }, [catchmentPoints, catchments, filters.departments, filters.displayActiveSDAGE, filters.useOnlyMainPoint, filters.watermass, qualitometers, filters.selectedPriorities])

    useActions(() => ({
        exportList: [{
            onClick: openExportModal,
            label: i18n.excel,
        }],
        // other: {
        //     other: (
        //         <Other
        //             icon='sync'
        //             tooltip='Calculer les tendances hype sur la période'
        //             onClick={() => {

        //             }}
        //         />
        //     ),
        // },
    }), [])

    const nitratesGradientGraphPercent = useMemo(() => {
        return generateGradient(INDICATORS_COLORS.YELLOW, errorColor, THRESHOLDS_NITRATES.length + 1)
    }, [])

    const gradientGraphPercent = useMemo(() => {
        return [
            generateGradient(INDICATORS_COLORS.YELLOW, errorColor, THRESHOLDS_PESTICIDES.length + 1),
            generateGradient(lightPink, IAEAU_SCALE_COLORS.VERY_BAD, THRESHOLDS_PESTICIDES.length + 1),
            generateGradient(JOB_COLOR.GREEN, mainBlue, THRESHOLDS_PESTICIDES.length + 1),
        ]
    }, [])

    const nbThresholdsEvol = useMemo(() => THRESHOLDS_EVOLUTION.length, [])
    const halfThresholdsEvol = useMemo(() => floor(nbThresholdsEvol / 2), [nbThresholdsEvol])
    const oddThresholdsEvol = useMemo(() => nbThresholdsEvol % 2 === 1 ? 1 : 0, [nbThresholdsEvol])
    const gradientGraphEvol = useMemo(() => {
        return [
            generateMultiGradient([
                [IAEAU_SCALE_COLORS.VERY_BAD, lightPink, halfThresholdsEvol],
                [INDICATORS_COLORS.YELLOW, INDICATORS_COLORS.YELLOW, 1],
                [JOB_COLOR.GREEN, mainBlue, halfThresholdsEvol + oddThresholdsEvol],
            ], false),
        ]
    }, [halfThresholdsEvol, oddThresholdsEvol])

    return (
        <Stack spacing={1} sx={{ margin: '10px 10px 100px 0' }} >
            <CardFilterEvolutionField
                defaultFilters={filters}
                onValidate={newFilters => {
                    setFilters(prev => ({ ...prev, ...newFilters }))
                    setParameters(newFilters.parameters)
                }}
            />
            <Card>
                <CardTitle title={i18n.nitratesMonitoring}/>
                <CardContent sx={{ padding: '10px 0 0 10px' }}>
                    <Grid container >
                        { !isNil(filters.secondStart) && !isNil(filters.secondEnd) && !isNaN(filters.secondStart) && !isNaN(filters.secondEnd) && (
                            <>
                                <Grid size={6}>
                                    <ParameterGraphicPercent
                                        parameter={NITRATES_CODE}
                                        ids={qualitoIds}
                                        filters={filters}
                                        colors={nitratesGradientGraphPercent}
                                        thresholds={THRESHOLDS_NITRATES}
                                        unit='mg/L'
                                        title={i18n.analysisOnFirstPeriod}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <ParameterGraphicEvolution
                                        parameter={NITRATES_CODE}
                                        ids={qualitoIds}
                                        filters={filters}
                                        colors={gradientGraphEvol[0]}
                                        thresholds={THRESHOLDS_EVOLUTION}
                                        title={i18n.comparisonBetweenPeriods}
                                    />
                                </Grid>
                            </>
                        ) || (
                            <>
                                <Grid offset={1.5} size={9}>
                                    <ParameterGraphicPercent
                                        parameter={NITRATES_CODE}
                                        ids={qualitoIds}
                                        filters={filters}
                                        colors={nitratesGradientGraphPercent}
                                        thresholds={THRESHOLDS_NITRATES}
                                        unit='mg/L'
                                        title={i18n.analysisOnFirstPeriod}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Card>
                <CardTitle title={i18n.sumPesticidesMonitoring}/>
                <CardContent sx={{ padding: '10px 0 0 10px' }}>
                    <Grid container >
                        <Grid size={6}>
                            <SumParameterGraphicPercent
                                ids={qualitoIds}
                                filters={filters}
                                colors={gradientGraphPercent[1]}
                                thresholds={THRESHOLDS_PESTICIDES}
                                title={i18n.analysisOnFirstPeriod}
                            />
                        </Grid>
                        <Grid size={6}>
                            <PieFamilyPesticides
                                ids={qualitoIds}
                                filters={filters}
                            />
                        </Grid>
                        { !isNil(filters.secondStart) && !isNil(filters.secondEnd) && !isNaN(filters.secondStart) && !isNaN(filters.secondEnd) && (
                            <>
                                <Grid offset={3} size={6}>
                                    <SumParameterGraphicEvolution
                                        ids={qualitoIds}
                                        filters={filters}
                                        colors={gradientGraphEvol[0]}
                                        thresholds={THRESHOLDS_EVOLUTION}
                                        title={i18n.comparisonBetweenPeriods}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            { filters.parameters?.length > 0 && (
                <Card>
                    <CardTitle title={(isNil(filters.secondStart) || isNil(filters.secondEnd) || isNaN(filters.secondStart) || isNaN(filters.secondEnd)) ? i18n.parametersAnalysisOnFirstPeriod : i18n.parametersQuantificationsEvolutionBetweenPeriods} />
                    <CardContent sx={{ padding: '10px 0 0 10px' }}>
                        <Grid container>
                            { !isNil(filters.secondStart) && !isNil(filters.secondEnd) && !isNaN(filters.secondStart) && !isNaN(filters.secondEnd) &&
                                filters.parameters.map(param => (
                                    <Grid key={param} size={4}>
                                        <ParameterGraphicEvolution
                                            parameter={param}
                                            ids={qualitoIds}
                                            filters={filters}
                                            colors={gradientGraphEvol[0]}
                                            thresholds={THRESHOLDS_EVOLUTION}
                                        />
                                    </Grid>
                                )) ||
                                filters.parameters.map((param, i) => (
                                    <Grid key={param} size={4}>
                                        <ParameterGraphicPercent
                                            parameter={param}
                                            ids={qualitoIds}
                                            filters={filters}
                                            colors={gradientGraphPercent[i % 3]}
                                            thresholds={THRESHOLDS_PESTICIDES}
                                            unit='mg/L'
                                        />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    </CardContent>
                </Card>
            )}
            <ExportModal
                isOpen={isExportModalOpen}
                close={closeExportModal}
                filters={filters}
            />
        </Stack>
    )
}

export default CatchmentsCompareApp